/**
 * WebSocket service for real-time updates
 */

import { authService } from './authService';

export interface WebSocketMessage {
  type: string;
  data?: any;
  timestamp?: string;
  connection_id?: string;
  user_id?: string;
  message?: string;
  topic?: string;
}

export interface WebSocketSubscription {
  topic: string;
  callback: (data: any) => void;
}

class WebSocketService {
  private ws: WebSocket | null = null;
  private subscriptions: Map<string, Set<(data: any) => void>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnecting = false;
  private connectionId: string | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  /**
   * Connect to WebSocket server
   */
  async connect(): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const token = authService.getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      // Construct WebSocket URL - use relative path from env or construct from current origin
      const baseWsUrl = process.env.REACT_APP_WS_URL || '/api/ws';
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      const wsUrl = baseWsUrl.startsWith('/')
        ? `${protocol}//${host}${baseWsUrl}/${token}`
        : `${baseWsUrl}/${token}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);

      // Wait for connection to open
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('WebSocket connection timeout'));
        }, 10000);

        this.ws!.onopen = (event) => {
          clearTimeout(timeout);
          this.handleOpen(event);
          resolve();
        };

        this.ws!.onerror = (event) => {
          clearTimeout(timeout);
          this.handleError(event);
          reject(new Error('WebSocket connection failed'));
        };
      });

    } catch (error) {
      this.isConnecting = false;
      throw error;
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }

    this.connectionId = null;
    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Subscribe to a topic
   */
  subscribe(topic: string, callback: (data: any) => void): () => void {
    if (!this.subscriptions.has(topic)) {
      this.subscriptions.set(topic, new Set());
    }

    this.subscriptions.get(topic)!.add(callback);

    // Send subscription message if connected
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.send({
        type: 'subscribe',
        topic: topic
      });
    } else if (authService.getToken()) {
      // Best-effort reconnect for pages that subscribe after another module disconnected.
      void this.connect().catch((error) => {
        console.error(`Failed to connect while subscribing to topic "${topic}":`, error);
      });
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribe(topic, callback);
    };
  }

  /**
   * Unsubscribe from a topic
   */
  unsubscribe(topic: string, callback: (data: any) => void): void {
    const callbacks = this.subscriptions.get(topic);
    if (callbacks) {
      callbacks.delete(callback);
      
      if (callbacks.size === 0) {
        this.subscriptions.delete(topic);
        
        // Send unsubscription message if connected
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.send({
            type: 'unsubscribe',
            topic: topic
          });
        }
      }
    }
  }

  /**
   * Send a message to the server
   */
  private send(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen(event: Event): void {
    console.log('WebSocket connected');
    this.isConnecting = false;
    this.reconnectAttempts = 0;

    // Start heartbeat
    this.startHeartbeat();

    // Resubscribe to all topics
    Array.from(this.subscriptions.keys()).forEach(topic => {
      this.send({
        type: 'subscribe',
        topic: topic
      });
    });
  }

  /**
   * Handle WebSocket message event
   */
  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);
      
      switch (message.type) {
        case 'connected':
          this.connectionId = message.connection_id || null;
          console.log('WebSocket connection established:', message);
          break;

        case 'subscription_confirmed':
          console.log('Subscribed to topic:', message.topic);
          break;

        case 'unsubscription_confirmed':
          console.log('Unsubscribed from topic:', message.topic);
          break;

        case 'pong':
          // Heartbeat response
          break;

        case 'dashboard_update':
        case 'conversation_update':
        case 'lead_update':
        case 'performance_alert':
        case 'system_status':
          this.handleTopicMessage(message);
          break;

        case 'new_message':
        case 'message_updated':
          this.dispatchToTopic('conversations', message);
          break;

        case 'error':
          console.error('WebSocket error:', message.message);
          break;

        default:
          console.log('Unknown message type:', message.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  }

  /**
   * Handle topic-specific messages
   */
  private handleTopicMessage(message: WebSocketMessage): void {
    const topicMap: Record<string, string> = {
      'dashboard_update': 'dashboard',
      'conversation_update': 'conversations',
      'lead_update': 'leads',
      'performance_alert': 'alerts',
      'system_status': 'system'
    };

    const topic = topicMap[message.type];
    if (topic) {
      this.dispatchToTopic(topic, message.data);
    }
  }

  /**
   * Dispatch payload to all subscribers of a topic
   */
  private dispatchToTopic(topic: string, payload: any): void {
    const callbacks = this.subscriptions.get(topic);
    if (!callbacks) {
      return;
    }

    callbacks.forEach(callback => {
      try {
        callback(payload);
      } catch (error) {
        console.error('Error in subscription callback:', error);
      }
    });
  }

  /**
   * Handle WebSocket close event
   */
  private handleClose(event: CloseEvent): void {
    console.log('WebSocket disconnected:', event.code, event.reason);
    
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    this.ws = null;
    this.connectionId = null;
    this.isConnecting = false;

    // Attempt to reconnect if not a normal closure
    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.attemptReconnect();
    }
  }

  /**
   * Handle WebSocket error event
   */
  private handleError(event: Event): void {
    console.error('WebSocket error:', event);
    this.isConnecting = false;
  }

  /**
   * Attempt to reconnect
   */
  private async attemptReconnect(): Promise<void> {
    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
    
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms`);
    
    setTimeout(async () => {
      try {
        await this.connect();
      } catch (error) {
        console.error('Reconnection failed:', error);
        
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.attemptReconnect();
        } else {
          console.error('Max reconnection attempts reached');
        }
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        this.send({ type: 'ping' });
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Get connection status
   */
  getConnectionStatus(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get connection info
   */
  getConnectionInfo(): any {
    return {
      status: this.getConnectionStatus(),
      connectionId: this.connectionId,
      reconnectAttempts: this.reconnectAttempts,
      subscriptions: Array.from(this.subscriptions.keys())
    };
  }
}

export const websocketService = new WebSocketService();

// Auto-connect when authenticated
authService.onAuthStateChange((isAuthenticated) => {
  if (isAuthenticated) {
    websocketService.connect().catch(console.error);
  } else {
    websocketService.disconnect();
  }
});
